import { Spin } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styled, { useTheme } from 'styled-components';
import type { Theme } from '../../styles/theme';
import { PreviewToolbar, usePreviewScale } from '../PreviewToolbar';
import type { ChangeKind, ICommentBox, IPageHighlights } from './types';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const setPdfWorkerSrc = (src: string): void => {
  pdfjs.GlobalWorkerOptions.workerSrc = src;
};

// wasmUrl points pdf.js at the openjpeg.wasm codec (copied to /wasm/ by
// library/postinstall.js) so JPX/JPEG2000-encoded images embedded in PDFs
// decode correctly instead of silently failing to render.
const DOCUMENT_OPTIONS = { wasmUrl: '/wasm/' };

export interface IPdfPreviewProps {
  url: string;
  fileName?: string;
  height?: number | string;
  /** Redline boxes for this document, keyed by 1-based page number. Opt-in — no prop, no overlay. */
  highlights?: Map<number, IPageHighlights>;
  /** Change currently navigated to; scrolled into view and ring-highlighted. */
  activeChangeId?: string | null;
  /** Click on a redline box. */
  onChangeSelect?: (changeId: string) => void;
  /** Persistent comment markers, keyed by 1-based page number. */
  commentBoxes?: Map<number, ICommentBox[]>;
  activeCommentId?: string | null;
  onCommentSelect?: (id: string) => void;
  /** Controlled zoom — supply both to sync zoom across two linked previews. */
  scale?: number;
  onScaleChange?: (scale: number) => void;
  /** Whether the thumbnails sidebar is initially visible. The toolbar button still toggles it. */
  showThumbnails?: boolean;
}

const THUMBNAIL_WIDTH = 90;

/** `#rrggbb`/`#rgb` → `rgba(r, g, b, alpha)`, for translucent overlay fills over theme colors. */
const withAlpha = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const fillForChange = (kind: ChangeKind, theme: Theme): string => {
  const light =
    kind === 'added'
      ? theme.colors.successLight
      : kind === 'removed'
        ? theme.colors.errorLight
        : theme.colors.warningLight;
  return withAlpha(light, 0.65);
};

const PdfPreview: React.FC<IPdfPreviewProps> = ({
  url,
  fileName = 'document.pdf',
  height = '100%',
  highlights,
  activeChangeId,
  onChangeSelect,
  commentBoxes,
  activeCommentId,
  onCommentSelect,
  scale: scaleProp,
  onScaleChange,
  showThumbnails: showThumbnailsDefault = false,
}) => {
  const theme = useTheme() as Theme;

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(showThumbnailsDefault);
  const [isLoading, setIsLoading] = useState(true);

  const { scale, setScale } = usePreviewScale(scaleProp, onScaleChange);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setIsLoading(false);
  };

  const scrollToPage = useCallback((pageNumber: number) => {
    pageRefs.current.get(pageNumber)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        let maxRatio = 0;
        let mostVisible = 0;
        for (const entry of entries) {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisible = parseInt(
              (entry.target as HTMLElement).dataset.pageNumber ?? '0',
              10
            );
          }
        }
        if (mostVisible > 0) setCurrentPage(mostVisible);
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    const refs = pageRefs.current;
    refs.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [numPages]);

  // Bring the navigated-to change into view. Only this document's own highlights
  // are consulted, so a change that exists solely on the other side leaves this
  // pane where it is rather than jumping somewhere arbitrary.
  useEffect(() => {
    if (!activeChangeId || !highlights || !numPages) return;

    for (const [pageNo, page] of highlights) {
      const box = page.boxes.find(b => b.changeId === activeChangeId);
      if (!box) continue;

      const el = pageRefs.current.get(pageNo);
      const root = scrollContainerRef.current;
      if (!el || !root) return;

      const target = el.offsetTop + box.rect.top * el.clientHeight - root.clientHeight / 3;
      root.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      return;
    }
  }, [activeChangeId, highlights, numPages, scale]);

  const handleRotate = () => setRotation(r => (r + 90) % 360);

  return (
    <Wrapper $height={height}>
      <PreviewToolbar
        scale={scale}
        onScaleChange={setScale}
        downloadUrl={url}
        fileName={fileName}
        currentPage={currentPage}
        numPages={numPages}
        onPageChange={scrollToPage}
        onToggleThumbnails={() => setShowThumbnails(v => !v)}
        onRotate={handleRotate}
      />

      <ContentArea>
        {showThumbnails && numPages > 0 && (
          <ThumbnailsSidebar>
            <Document file={url} loading="">
              {pages.map(n => (
                <ThumbnailWrapper
                  key={n}
                  $active={n === currentPage}
                  onClick={() => scrollToPage(n)}
                >
                  <Page
                    pageNumber={n}
                    width={THUMBNAIL_WIDTH}
                    rotate={rotation}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  <ThumbnailLabel $active={n === currentPage}>
                    {n}
                  </ThumbnailLabel>
                </ThumbnailWrapper>
              ))}
            </Document>
          </ThumbnailsSidebar>
        )}

        <MainScroll ref={scrollContainerRef}>
          {isLoading && (
            <SpinWrapper>
              <Spin size="large" />
            </SpinWrapper>
          )}
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading=""
            options={DOCUMENT_OPTIONS}
          >
            {pages.map(n => (
              <PageWrapper
                key={n}
                ref={el => {
                  if (el) pageRefs.current.set(n, el);
                  else pageRefs.current.delete(n);
                }}
                data-page-number={n}
              >
                <Page pageNumber={n} scale={scale} rotate={rotation} />
                {/* Redline sits above the canvas but below nothing else — the
                    text layer stays selectable through the transparent layer.
                    Suppressed on rotation: the rects are normalised against the
                    unrotated page and would land in the wrong place otherwise. */}
                {highlights && rotation === 0 && (
                  <DiffOverlayLayer
                    page={highlights.get(n)}
                    theme={theme}
                    activeChangeId={activeChangeId}
                    onSelect={onChangeSelect}
                  />
                )}
                {commentBoxes && rotation === 0 && (
                  <CommentLayer
                    boxes={commentBoxes.get(n)}
                    theme={theme}
                    activeCommentId={activeCommentId}
                    onSelect={onCommentSelect}
                  />
                )}
              </PageWrapper>
            ))}
          </Document>
        </MainScroll>
      </ContentArea>
    </Wrapper>
  );
};

export default PdfPreview;

/** Redline boxes for a single rendered page. Click-through except on the boxes
 * themselves, so text selection on the page below keeps working. */
const DiffOverlayLayer: React.FC<{
  page: IPageHighlights | undefined;
  theme: Theme;
  activeChangeId?: string | null;
  onSelect?: (changeId: string) => void;
}> = ({ page, theme, activeChangeId, onSelect }) => {
  if (!page?.boxes.length) return null;

  return (
    <OverlayLayer aria-hidden={!onSelect}>
      {page.boxes.map((box, i) => {
        const active = box.changeId === activeChangeId;
        return (
          <OverlayBox
            key={`${box.changeId}-${i}`}
            type="button"
            data-change-id={box.changeId}
            title={box.kind}
            onClick={onSelect ? () => onSelect(box.changeId) : undefined}
            style={{
              left: `${box.rect.left * 100}%`,
              top: `${box.rect.top * 100}%`,
              width: `${box.rect.width * 100}%`,
              height: `${box.rect.height * 100}%`,
              background: fillForChange(box.kind, theme),
              cursor: onSelect ? 'pointer' : 'default',
              pointerEvents: onSelect ? 'auto' : 'none',
              boxShadow: active ? `0 0 0 2px ${theme.colors.primary}` : 'none',
            }}
          />
        );
      })}
    </OverlayLayer>
  );
};

/** Persistent comment highlights for one page — a marker box hugging each
 * commented passage. A distinct purple step (not `primary`) so comments read
 * apart from the accent color used for buttons/active states elsewhere. */
const CommentLayer: React.FC<{
  boxes: ICommentBox[] | undefined;
  theme: Theme;
  activeCommentId?: string | null;
  onSelect?: (id: string) => void;
}> = ({ boxes, theme, activeCommentId, onSelect }) => {
  if (!boxes?.length) return null;
  const fill = withAlpha(theme.colors.purple[200], 0.55);
  const ink = theme.colors.purple[700];

  return (
    <OverlayLayer>
      {boxes.map((b, i) => {
        const active = b.id === activeCommentId;
        return (
          <OverlayBox
            key={`${b.id}-${i}`}
            type="button"
            title="Comment"
            onClick={onSelect ? () => onSelect(b.id) : undefined}
            style={{
              left: `${b.rect.left * 100}%`,
              top: `${b.rect.top * 100}%`,
              width: `${b.rect.width * 100}%`,
              height: `${b.rect.height * 100}%`,
              background: fill,
              boxShadow: active ? `0 0 0 2px ${ink}` : `inset 0 -2px 0 ${ink}`,
              cursor: onSelect ? 'pointer' : 'default',
              pointerEvents: onSelect ? 'auto' : 'none',
            }}
          />
        );
      })}
    </OverlayLayer>
  );
};

interface IWrapperProps {
  $height: number | string;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  flex-direction: column;
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const ThumbnailsSidebar = styled.div`
  width: 120px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.sm} 0;

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

interface IThumbnailProps {
  $active: boolean;
}

const ThumbnailWrapper = styled.div<IThumbnailProps>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  .react-pdf__Page {
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const ThumbnailLabel = styled.span<IThumbnailProps>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const MainScroll = styled.div`
  flex: 1;
  overflow: auto;
  background: ${({ theme }) => theme.colors.background};
  position: relative;

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    width: fit-content;
    min-width: 100%;
    padding: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const PageWrapper = styled.div`
  position: relative;
  margin: 0 auto;
  box-shadow: ${({ theme }) => theme.shadows.md};

  .react-pdf__Page {
    display: block;
  }
`;

const SpinWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: ${({ theme }) => theme.colors.background};
`;

const OverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

const OverlayBox = styled.button`
  position: absolute;
  border: none;
  padding: 0;
  border-radius: 2px;
  outline: none;
  transition: box-shadow 0.12s ease;
`;
